import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { avisar, textoDeError } from '../components/Dialogo';
import { PeriodPicker, periodoQS, periodoTexto } from '../components/PeriodPicker';
import { usePeriodoInicial } from '../lib/preferencias';
import { rolEtiqueta } from '../lib/roles';
import { descargarExcel, nombreArchivo, Celda } from '../lib/excel';

/**
 * Administración → ACCESOS (0.50.0): la bitácora de accesos del personal de las TRES apps (Dra. IA, DTB HUB y
 * recepción), `hub_accesos` (api/src/lib/accesos.ts). Quién entró, quién lo intentó y no pudo (y por qué), qué
 * expediente abrió cada quien y qué cambió. Sólo admin; API `GET panel/accesos` (ninguna ruta empieza con `admin`).
 * Lista de 50 en 50 con la fecha y hora de CDMX, filtros (periodo, app, persona, paciente, sólo fallidos), arriba un
 * resumen y "Bajar a Excel" con TODO lo filtrado (hasta 5,000).
 */
interface Fila {
  id: string; at: string; app: string; user_id: string | null; rol: string | null; accion: string; patient_id: string | null;
  detalle: Record<string, any> | null; ip: string | null; ok: boolean;
  persona: string | null; paciente: string | null; paciente_borrado: boolean | null; cuenta_nombre: string | null;
}
interface Repetido { quien: string; user_id: string | null; persona: string | null; correo: string | null; n: number; ips: number; ultimo: string; motivo: string | null; apps: string[] }
interface Resp {
  disponible: boolean; motivo?: string; filas: Fila[]; total: number; pagina: number; por: number; paginas: number; recortado: boolean;
  resumen: { entradas: number; fallidos: number; vistas: number; expedientes: number; cambios: number; personas: number; repetidos: Repetido[] } | null;
  personas: { user_id: string; nombre: string; rol: string }[]; repetidos_desde?: number;
}

export const APP_TEXTO: Record<string, string> = { draia: 'Dra. IA', hub: 'DTB HUB', recepcion: 'Recepción' };
const MOTIVO_TEXTO: Record<string, string> = {
  contrasena: 'contraseña', sin_cuenta: 'el correo no es de ninguna cuenta', sin_acceso_a_la_app: 'la cuenta no tiene acceso a esta app',
  inactiva: 'la cuenta está desactivada', sin_contrasena: 'la cuenta no tiene contraseña',
};
const CAMPO_TEXTO: Record<string, string> = {
  nombre: 'nombre', rol: 'rol', activo: 'activa o no', password: 'contraseña', contrasena: 'contraseña', email: 'correo', telefono: 'teléfono', atiende: 'atiende pacientes',
  tipo_cliente: 'tipo de cliente', etapa: 'etapa',
  // los del expediente en Dra. IA (llegan con el nombre de su campo)
  sexo: 'sexo', fecha_nacimiento: 'fecha de nacimiento', edad: 'edad', profesion: 'a qué se dedica', estado_civil: 'estado civil',
  hijos: 'hijas o hijos', phone: 'teléfono', phone2: 'otro teléfono', sucursal: 'sucursal', notas: 'notas', objetivo_mejora: 'lo que quiere mejorar',
};
const CAMBIO_TEXTO: Record<string, string> = {
  datos_paciente: 'Cambió los datos del expediente', borrar_expediente: 'Borró el expediente',
  nota: 'Escribió una nota', editar_nota: 'Editó una nota', borrar_nota: 'Borró una nota',
  documento: 'Subió un documento', borrar_documento: 'Borró un documento',
  consulta_nueva: 'Registró una consulta', consulta: 'Editó una consulta', borrar_consulta: 'Borró una consulta',
  historia_clinica: 'Generó la historia clínica', editar_historia: 'Editó la historia clínica',
  protocolo_nuevo: 'Guardó un protocolo', protocolo: 'Editó un protocolo', borrar_protocolo: 'Borró un protocolo',
  juntar_expedientes: 'Juntó dos expedientes', alta_cuenta: 'Dio de alta una cuenta del personal', pin_reiniciado: 'Reinició el PIN de otra cuenta',
  cuenta_personal: 'Cambió una cuenta del personal', permisos: 'Cambió los permisos por sección',
};

/** La acción en palabras. */
export function accionTexto(f: Pick<Fila, 'accion' | 'detalle' | 'cuenta_nombre'>): string {
  const d = f.detalle || {};
  if (f.accion === 'login_ok') return 'Entró';
  if (f.accion === 'login_fallido') return `Intento fallido: ${MOTIVO_TEXTO[d.motivo] || d.motivo || 'sin motivo'}`;
  if (f.accion === 'vio_expediente') return 'Abrió el expediente';
  if (f.accion === 'vio_ficha') return 'Abrió la Ficha';
  if (f.accion === 'vio_persona') return d.via === 'qr' ? 'Escaneó su QR' : d.via === 'historial' ? 'Abrió su historial' : 'Abrió su expediente';
  if (f.accion.startsWith('cambio:')) {
    const que = f.accion.slice(7);
    if (que === 'cuenta_prueba') return d.prueba ? 'Marcó el expediente como cuenta de prueba' : 'Quitó la marca de cuenta de prueba';
    const campos = Array.isArray(d.campos) ? d.campos.map((c: string) => CAMPO_TEXTO[c] || c) : [];
    if (que === 'cuenta_personal') return `Cambió la cuenta de ${f.cuenta_nombre || 'otra persona'}${campos.length ? `: ${campos.join(', ')}` : ''}`;
    if (que === 'alta_cuenta') return `Dio de alta la cuenta de ${f.cuenta_nombre || 'otra persona'}`;
    if (que === 'pin_reiniciado') return `Reinició el PIN de ${f.cuenta_nombre || 'otra cuenta'}`;
    // Administración → Permisos (0.55.0): qué abre cada rol o persona.
    if (que === 'permisos') {
      const r = Array.isArray(d.roles) ? d.roles.map((x: string) => rolEtiqueta(x)) : [];
      const n = Number(d.personas) || 0;
      return `Cambió los permisos por sección${r.length ? ` de ${r.join(', ')}` : ''}${n ? `${r.length ? ' y' : ' de'} ${n === 1 ? 'una persona' : `${n} personas`}` : ''}`;
    }
    // Mi plan de control de peso (Dra. IA 2.59.0): el programa, sus aplicaciones, el InBody y los avisos al médico.
    if (que === 'peso_programa') {
      const q = String(d.que || '');
      if (q === 'activar') return 'Activó su programa de control de peso';
      if (q === 'terminar') return 'Terminó su programa de control de peso';
      const PROG: Record<string, string> = { dosis_mg: 'dosis', fase: 'fase', dia_inyeccion: 'día de inyección', hora_inyeccion: 'hora',
        inicio: 'inicio', metas: 'metas', proximo_inbody: 'próximo InBody', responsable_user_id: 'quién lo lleva', recordatorio: 'recordatorio',
        umbrales: 'límites de avisos' };
      const qs = q.split(',').filter(Boolean).map((x) => PROG[x] || x.replace(/_/g, ' '));
      return `Cambió su programa de control de peso${qs.length ? `: ${qs.join(', ')}` : ''}`;
    }
    if (que === 'peso_aplicacion') return d.que === 'corregir' ? 'Corrigió una aplicación de su control de peso' : 'Anotó una aplicación de su control de peso';
    if (que === 'peso_inbody') return 'Capturó un InBody de su control de peso';
    if (que === 'peso_aviso') return d.estado === 'atendido' ? 'Atendió un aviso de su control de peso' : d.estado === 'visto' ? 'Vio un aviso de su control de peso' : 'Cambió un aviso de su control de peso';
    const base = CAMBIO_TEXTO[que] || `Cambio: ${que.replace(/_/g, ' ')}`;
    return que === 'datos_paciente' && campos.length ? `${base}: ${campos.join(', ')}` : base;
  }
  return f.accion;
}
const fechaHora = (iso: string) => new Date(iso).toLocaleString('es-MX', { timeZone: 'America/Mexico_City', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
const personaTexto = (f: Pick<Fila, 'persona' | 'user_id' | 'detalle'>) =>
  f.persona || (f.user_id ? 'Cuenta borrada' : f.detalle?.correo ? 'Correo que no es de ninguna cuenta' : 'Sin cuenta');
const num = (n: number | undefined | null) => (Number(n) || 0).toLocaleString('es-MX');

const celda: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
const inputCss: React.CSSProperties = { padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'inherit', fontSize: 13, width: 'auto', maxWidth: '100%' };

export function Accesos({ qs, tenantSel }: { qs: string; tenantSel: string }) {
  const [periodo, setPeriodo] = useState(usePeriodoInicial());
  const [app, setApp] = useState('');
  const [persona, setPersona] = useState('');
  const [paciente, setPaciente] = useState<{ id: string; nombre: string } | null>(null);
  const [fallidos, setFallidos] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [d, setD] = useState<Resp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  const [bajando, setBajando] = useState(false);
  // El buscador de paciente: espera a que se deje de teclear.
  const [q, setQ] = useState('');
  const [sugeridos, setSugeridos] = useState<{ patient_id: string; nombre: string; sucursal: string | null }[]>([]);
  const ultima = useRef(0);

  const tenantQS = qs.replace(/^\?/, '');
  const armar = (extra: Record<string, string> = {}) => {
    const p = new URLSearchParams(periodoQS(periodo));
    if (tenantQS) for (const [k, v] of new URLSearchParams(tenantQS)) p.set(k, v);
    if (app) p.set('app', app);
    if (persona) p.set('user_id', persona);
    if (paciente) p.set('patient_id', paciente.id);
    if (fallidos) p.set('fallidos', '1');
    for (const [k, v] of Object.entries(extra)) p.set(k, v);
    return p.toString();
  };

  useEffect(() => { setPagina(1); }, [periodo, app, persona, paciente, fallidos, qs, tenantSel]);
  useEffect(() => {
    const n = ++ultima.current;
    setCargando(true); setError(null);
    api<Resp>(`panel/accesos?${armar({ pagina: String(pagina), por: '50' })}`)
      .then((r) => { if (n === ultima.current) setD(r); })
      .catch((e) => { if (n === ultima.current) setError(textoDeError(e)); })
      .finally(() => { if (n === ultima.current) setCargando(false); });
  }, [periodo, app, persona, paciente, fallidos, pagina, qs, tenantSel]);

  useEffect(() => {
    const v = q.trim();
    if (v.length < 2) { setSugeridos([]); return; }
    const t = setTimeout(() => {
      api<{ pacientes: { patient_id: string; nombre: string; sucursal: string | null }[] }>(`panel/accesos/pacientes?${new URLSearchParams({ ...(tenantQS ? Object.fromEntries(new URLSearchParams(tenantQS)) : {}), q: v })}`)
        .then((r) => setSugeridos(r.pacientes || [])).catch(() => setSugeridos([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q, qs]);

  const bajar = async () => {
    setBajando(true);
    try {
      const r = await api<Resp>(`panel/accesos?${armar({ todo: '1' })}`);
      if (!r.filas.length) { await avisar({ titulo: 'No hay nada que bajar', mensaje: 'Con esos filtros no queda ningún acceso.' }); return; }
      const filas: Celda[][] = r.filas.map((f) => [new Date(f.at), personaTexto(f), f.rol ? rolEtiqueta(f.rol) : '', APP_TEXTO[f.app] || f.app,
        accionTexto(f), f.patient_id ? (f.paciente || 'Expediente sin nombre') : '', f.ip || '', f.ok ? 'bien' : 'fallido']);
      descargarExcel(nombreArchivo('accesos', periodoTexto(periodo)), [{ nombre: 'Accesos', columnas: [
        { titulo: 'Fecha y hora', tipo: 'fechahora', ancho: 20 }, { titulo: 'Persona del equipo', ancho: 28 }, { titulo: 'Rol', ancho: 18 },
        { titulo: 'App', ancho: 12 }, { titulo: 'Acción', ancho: 44 }, { titulo: 'Paciente', ancho: 30 }, { titulo: 'IP', ancho: 16 }, { titulo: 'Resultado', ancho: 10 },
      ], filas }]);
      if (r.recortado) await avisar({ titulo: 'Se bajaron los primeros 5,000', mensaje: `Con esos filtros hay ${num(r.total)} accesos; el archivo trae los 5,000 más recientes. Acota el periodo para bajar el resto.` });
    } catch (e) { await avisar({ titulo: 'No se pudo bajar', mensaje: textoDeError(e), tipo: 'error' }); }
    finally { setBajando(false); }
  };

  const res = d?.resumen;
  const desde = d ? (d.pagina - 1) * d.por + 1 : 0;
  const hasta = d ? Math.min(d.total, d.pagina * d.por) : 0;
  return (
    <div data-seccion="accesos" style={{ display: 'grid', gap: 12, minWidth: 0 }}>
      <p className="muted" style={{ margin: '0 2px', fontSize: 13, lineHeight: 1.5 }}>
        Quién entró a Dra. IA, DTB HUB y recepción, quién lo intentó y no pudo, qué expediente abrió cada quien y qué cambió.
        Nunca se guardan contraseñas, PIN ni datos clínicos o de contacto: sólo quién, qué y cuándo.
      </p>
      <div className="card" style={{ padding: 12, display: 'grid', gap: 10, minWidth: 0 }}>
        <PeriodPicker value={periodo} onChange={setPeriodo} sinPruebas />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', minWidth: 0 }}>
          <select value={app} onChange={(e) => setApp(e.target.value)} style={inputCss} aria-label="App" data-filtro-app>
            <option value="">Las tres apps</option>
            {Object.entries(APP_TEXTO).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
          </select>
          <select value={persona} onChange={(e) => setPersona(e.target.value)} style={{ ...inputCss, maxWidth: 240 }} aria-label="Persona del equipo" data-filtro-persona>
            <option value="">Todo el equipo</option>
            {(d?.personas || []).map((p) => <option key={p.user_id} value={p.user_id}>{p.nombre} · {rolEtiqueta(p.rol)}</option>)}
          </select>
          {paciente ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 999, background: 'var(--gold-light)', color: 'var(--gold-dark)', fontSize: 13, maxWidth: '100%' }} data-filtro-paciente-elegido>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Paciente: {paciente.nombre}</span>
              <button type="button" className="btn-ghost" onClick={() => setPaciente(null)} aria-label="Quitar el filtro de paciente" style={{ padding: '0 6px', fontSize: 13, boxShadow: 'none' }}>✕</button>
            </span>
          ) : (
            <div style={{ position: 'relative', minWidth: 0, flex: '1 1 200px', maxWidth: 320 }}>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Paciente (buscar por nombre)" aria-label="Buscar paciente" style={{ ...inputCss, width: '100%' }} data-filtro-paciente />
              {sugeridos.length > 0 && (
                <div className="card" role="listbox" aria-label="Pacientes" style={{ position: 'absolute', zIndex: 20, left: 0, right: 0, top: '100%', marginTop: 4, padding: 4, maxHeight: 260, overflow: 'auto' }} data-sugeridos>
                  {sugeridos.map((p) => (
                    <button key={p.patient_id} type="button" role="option" aria-selected={false} className="btn-ghost"
                      onClick={() => { setPaciente({ id: p.patient_id, nombre: p.nombre }); setQ(''); setSugeridos([]); }}
                      style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', boxShadow: 'none', padding: '7px 9px', fontSize: 13 }}>
                      {p.nombre}{p.sucursal ? <span className="muted"> · {p.sucursal}</span> : null}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={fallidos} onChange={(e) => setFallidos(e.target.checked)} style={{ width: 'auto' }} data-filtro-fallidos /> Sólo intentos fallidos
          </label>
          <button type="button" className="btn-ghost" style={{ marginLeft: 'auto' }} onClick={bajar} disabled={bajando || !d?.disponible || !d.total} data-excel-accesos>
            {bajando ? 'Bajando…' : 'Bajar a Excel'}</button>
        </div>
      </div>

      {error && <div className="card" role="alert" style={{ padding: '10px 12px', background: 'var(--warning-bg)', color: 'var(--warning)', border: 'none', fontSize: 13 }}>{error}</div>}
      {d && !d.disponible && <div className="card" style={{ padding: '10px 12px', background: 'var(--warning-bg)', color: 'var(--warning)', border: 'none', fontSize: 13 }} data-sin-bitacora>{d.motivo}</div>}

      {res && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: 10 }} data-resumen-accesos>
          {[
            ['Entradas', num(res.entradas), `${num(res.personas)} ${res.personas === 1 ? 'persona' : 'personas'}`, 'entradas'],
            ['Intentos fallidos', num(res.fallidos), res.repetidos.length ? `${res.repetidos.length} se ${res.repetidos.length === 1 ? 'repite' : 'repiten'} mucho` : 'ninguno se repite mucho', 'fallidos'],
            ['Expedientes abiertos', num(res.expedientes), `${num(res.vistas)} ${res.vistas === 1 ? 'vez' : 'veces'}`, 'expedientes'],
            ['Cambios', num(res.cambios), 'datos, notas, cuentas…', 'cambios'],
          ].map(([t, v, sub, k]) => (
            <div key={k} className="card" style={{ padding: '10px 12px', minWidth: 0 }} data-kpi={k}>
              <div className="muted" style={{ fontSize: 12 }}>{t}</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: k === 'fallidos' && res.fallidos ? 'var(--warning)' : 'var(--gold-dark)' }}>{v}</div>
              <div className="muted" style={{ fontSize: 12 }}>{sub}</div>
            </div>
          ))}
        </div>
      )}
      {res && res.repetidos.length > 0 && (
        <div className="card" style={{ padding: '10px 12px', background: 'var(--warning-bg)', color: 'var(--warning)', border: 'none', fontSize: 13, display: 'grid', gap: 4 }} data-repetidos>
          <b>Intentos fallidos que se repiten ({d?.repetidos_desde || 5} o más en el periodo)</b>
          {res.repetidos.map((r) => (
            <div key={r.quien}>
              {r.n} intentos {r.user_id ? <>a la cuenta de <b>{r.persona || 'una cuenta borrada'}</b></> : <>con un correo que no es de ninguna cuenta <span style={{ opacity: 0.75 }}>(huella {r.correo || '?'})</span></>}
              {r.motivo ? ` · ${MOTIVO_TEXTO[r.motivo] || r.motivo}` : ''} · desde {r.ips} {r.ips === 1 ? 'IP' : 'IP distintas'} · el último {fechaHora(r.ultimo)}
              {r.apps?.length ? ` · ${r.apps.map((a) => APP_TEXTO[a] || a).join(', ')}` : ''}
            </div>
          ))}
        </div>
      )}

      {d?.disponible && (
        <div className="card tabla-scroll" style={{ padding: 0, overflowX: 'auto', minWidth: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-tabla-accesos>
            <thead><tr>{['Fecha y hora', 'Persona del equipo', 'App', 'Acción', 'Paciente', 'IP'].map((h) => (
              <th key={h} style={{ textAlign: 'left', padding: '9px 12px', borderBottom: '2px solid var(--border)', color: 'var(--ink-soft)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>))}
            </tr></thead>
            <tbody>
              {d.filas.map((f) => (
                <tr key={f.id} data-acceso={f.accion} style={f.ok ? undefined : { background: 'var(--warning-bg)' }}>
                  <td style={{ ...celda, whiteSpace: 'nowrap' }}>{fechaHora(f.at)}</td>
                  <td style={celda}>
                    <span style={{ fontWeight: 600 }}>{personaTexto(f)}</span>
                    {f.rol && <span className="muted" style={{ display: 'block', fontSize: 11.5 }}>{rolEtiqueta(f.rol)}</span>}
                    {!f.user_id && f.detalle?.correo && <span className="muted" style={{ display: 'block', fontSize: 11 }}>huella {f.detalle.correo}</span>}
                  </td>
                  <td style={{ ...celda, whiteSpace: 'nowrap' }}>{APP_TEXTO[f.app] || f.app}</td>
                  <td style={{ ...celda, color: f.ok ? undefined : 'var(--warning)', minWidth: 180 }}>{accionTexto(f)}</td>
                  <td style={{ ...celda, minWidth: 140 }}>
                    {f.patient_id ? (f.paciente && !f.paciente_borrado
                      ? <Link to={`/paciente/${f.patient_id}`} style={{ color: 'var(--gold-dark)' }}>{f.paciente}</Link>
                      : <span className="muted">{f.paciente || 'Expediente'}{f.paciente_borrado ? ' (borrado o juntado)' : ''}</span>)
                      : <span className="muted">—</span>}
                  </td>
                  <td className="muted" style={{ ...celda, whiteSpace: 'nowrap', fontSize: 12 }}>{f.ip || '—'}</td>
                </tr>
              ))}
              {!d.filas.length && (
                <tr><td colSpan={6} className="muted" style={{ ...celda, textAlign: 'center', padding: 24 }} data-vacio>
                  {cargando ? 'Cargando…' : `No hay accesos ${fallidos ? 'fallidos ' : ''}con esos filtros (${periodoTexto(periodo).toLowerCase()}).`}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {d?.disponible && d.total > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }} data-paginas>
          <span className="muted" style={{ fontSize: 13 }}>{num(desde)}–{num(hasta)} de {num(d.total)}</span>
          <button type="button" className="btn-ghost" disabled={cargando || d.pagina <= 1} onClick={() => setPagina((x) => Math.max(1, x - 1))} data-anterior>Anterior</button>
          <button type="button" className="btn-ghost" disabled={cargando || d.pagina >= d.paginas} onClick={() => setPagina((x) => x + 1)} data-siguiente>Siguiente</button>
        </div>
      )}
      {!d && !error && <div className="muted" style={{ padding: 24, textAlign: 'center' }}>Cargando accesos…</div>}
    </div>
  );
}
